import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ComingSoonTooltip from '@/components/ui/custom/dataDisplay/ComingSoonTooltip';
import {
  Button,
  Label,
  RadialProgress,
  Tabs,
  TextInput,
  Toggle,
} from '@/components/ui/daisy';
import useUploading from '@/data/hook/shared/useUploading';
import { Nullable } from '@/models/Model';
import Resource from '@/models/Resource';
import { resourceToFields } from '@/utils/resource/resourceToFields';
import { ServiceUploadingOptions } from '@/utils/shared/uploadProgress';
import isValidUrl from '@/utils/validators/isValidUrl';

import ResourceFileField from './ResourceFileField';

export enum ResourceTypeEnum {
  LINK = 'LINK',
  FILE = 'FILE',
}

export type ResourceFormFields = {
  title: string;
  type: ResourceTypeEnum;
  resourceLink: string;
  resourceFile: Nullable<File>;
  isMandatory: boolean;
  onlyTeacher: boolean;
};

type ResourceInlineFormProps = {
  resource?: Resource;
  isLoading?: boolean;
  onSubmit: (
    data: ResourceFormFields,
    options: ServiceUploadingOptions,
  ) => Promise<unknown>;
  onCancel: () => void;
};

export default function ResourceInlineForm({
  resource,
  isLoading,
  onSubmit,
  onCancel,
}: ResourceInlineFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourceForm.form',
  });

  const defaultValues = useMemo(() => resourceToFields(resource), [resource]);

  const {
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<ResourceFormFields>({ defaultValues });

  const typeValue = watch('type');

  const { uploadProgress, handleSubmit: handleUploadSubmit } =
    useUploading<ResourceFormFields>(onSubmit);

  if (uploadProgress != null) {
    return <UploadingView uploadProgress={uploadProgress} />;
  }

  return (
    <FieldsetContainer>
      <form
        onSubmit={handleSubmit(handleUploadSubmit)}
        className="flex flex-col gap-4"
      >
        <TextInput.Fieldset label={t('title')} labelAlt={errors.title?.message}>
          <TextInput
            type="text"
            placeholder={t('titlePlaceholder')}
            className="w-full"
            color={errors.title ? 'error' : 'default'}
            {...register('title', { required: t('titleRequired') })}
          />
        </TextInput.Fieldset>

        <div className="flex items-start gap-2">
          <ConditionalRenderer condition={typeValue === ResourceTypeEnum.LINK}>
            <TextInput.Fieldset
              className="flex-1"
              label={t('link')}
              labelAlt={errors.resourceLink?.message}
            >
              <TextInput
                type="text"
                placeholder={t('linkPlaceholder')}
                className="w-full"
                color={errors.resourceLink ? 'error' : 'default'}
                {...register('resourceLink', {
                  validate: value => {
                    if (typeValue !== ResourceTypeEnum.LINK) return true;
                    if (!value?.trim()) return t('linkRequired');
                    if (!isValidUrl(value)) return t('linkInvalid');
                    return true;
                  },
                })}
              />
            </TextInput.Fieldset>
          </ConditionalRenderer>
          <ConditionalRenderer condition={typeValue === ResourceTypeEnum.FILE}>
            <ResourceFileField control={control} resource={resource} />
          </ConditionalRenderer>
          <Tabs
            variant="box"
            value={typeValue}
            className="mt-7.5"
            onChange={value =>
              setValue('type', value as ResourceTypeEnum, {
                shouldDirty: true,
              })
            }
          >
            <Tabs.Tab value={ResourceTypeEnum.LINK}>{t('typeLink')}</Tabs.Tab>
            <Tabs.Tab value={ResourceTypeEnum.FILE}>{t('typeFile')}</Tabs.Tab>
          </Tabs>
        </div>

        <Label>
          <Toggle color="primary" {...register('isMandatory')} />
          {t('isMandatory')}
        </Label>

        <ComingSoonTooltip disabled>
          <Label>
            <Toggle color="primary" disabled />
            {t('allowDownload')}
          </Label>
        </ComingSoonTooltip>

        <Label>
          <Toggle color="primary" {...register('onlyTeacher')} />
          {t('onlyTeacher')}
        </Label>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="grow bg-base-300"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="secondary"
            className="grow"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </FieldsetContainer>
  );
}

type UploadingViewProps = {
  uploadProgress: number;
};

function UploadingView({ uploadProgress }: UploadingViewProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourceForm.form',
  });

  return (
    <FieldsetContainer>
      <div className="flex flex-col items-center justify-center gap-4">
        <RadialProgress
          value={uploadProgress}
          color="primary"
          size="md"
          thickness="md"
          classNames={{ base: 'text-base text-primary' }}
        >
          {uploadProgress}%
        </RadialProgress>
        <p className="text-sm text-base-content text-center">
          {t('uploadingFile')}
        </p>
        <div className="flex justify-end gap-4 w-full">
          <Button type="button" className="grow bg-base-300" disabled>
            {t('cancel')}
          </Button>
          <Button type="button" color="secondary" className="grow" disabled>
            {t('save')}
          </Button>
        </div>
      </div>
    </FieldsetContainer>
  );
}
